import { HttpInterceptorFn, HttpResponse } from '@angular/common/http'
import { tap } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/**
 * HTTP Logger Interceptor
 *
 * Logs detailed information about HTTP requests and responses to aid in debugging,
 * especially when browsers show misleading error messages like CORS errors.
 */
export const httpLoggerInterceptor: HttpInterceptorFn = (req, next) => {
  // Add console error interceptor on initialization
  interceptConsoleForCorsErrors()

  // Skip logging in production
  const isProduction = environment.production
  if (isProduction) {
    return next(req)
  }

  // Generate a unique request ID to correlate request and response logs
  const requestId = Math.random().toString(36).substring(2, 10)

  // Log the outgoing request
  const requestTime = new Date().toISOString()
  console.group(
    `🌐 HTTP Request [${requestId}] ${req.method} ${req.url} at ${requestTime}`,
  )
  console.log('Headers:', req.headers)

  if (req.body) {
    console.log('Body:', req.body)
  }
  console.groupEnd()

  // Return the request and log the response
  return next(req).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          const responseTime = new Date().toISOString()
          const duration =
            new Date().getTime() - new Date(requestTime).getTime()

          console.group(
            `✅ HTTP Response [${requestId}] ${req.method} ${req.url} (${duration}ms) at ${responseTime}`,
          )
          console.log('Status:', event.status, event.statusText)
          console.log('Headers:', event.headers)

          // Try to determine if it's JSON to format nicely
          try {
            if (typeof event.body === 'object') {
              console.log('Body:', event.body)
            } else {
              console.log('Body:', event.body)
            }
          } catch (e) {
            console.log('Body:', event.body)
          }
          console.groupEnd()
        }
      },
      error: (error) => {
        const responseTime = new Date().toISOString()
        const duration = new Date().getTime() - new Date(requestTime).getTime()

        console.group(
          `❌ HTTP Error [${requestId}] ${req.method} ${req.url} (${duration}ms) at ${responseTime}`,
        )
        console.log('Status:', error.status, error.statusText || 'Unknown')
        console.log('Message:', error.message || 'No message')

        // Check if this is a misleading CORS error
        if (error.message && error.message.includes('CORS')) {
          console.warn('⚠️ Possible misleading CORS error!')
          console.warn(
            'This might not be a real CORS issue but a different error presented as CORS.',
          )
          console.warn(
            'Check if the server is reachable, if the endpoint exists, or if authentication failed.',
          )
        }

        // Log the error details
        if (error.error) {
          console.log('Error details:', error.error)
        }

        console.groupEnd()
      },
    }),
  )
}

// Flag to track if we've already installed the console interceptor
let consoleInterceptorInstalled = false

// List of recent real CORS errors we've determined to be genuine
const genuineCorsErrors = new Set<string>()

// Regular expressions for detection
const CORS_ERROR_PATTERN =
  /Access to XMLHttpRequest at .+ has been blocked by CORS policy/
const URL_PATTERN = /XMLHttpRequest at '([^']+)'/
const ORIGIN_PATTERN = /from origin '([^']+)'/

// Type for the file tracker
interface FileTracker {
  lastFile: string
  setFile(file: string): void
}

// Type for suppressed error messages
interface SuppressedMessage {
  timestamp: string
  message: string
  url: string
  origin: string
}

/**
 * Intercepts console.error messages and filters out misleading CORS errors
 */
function interceptConsoleForCorsErrors() {
  // Don't install multiple times
  if (consoleInterceptorInstalled) {
    return
  }

  consoleInterceptorInstalled = true

  // Save the original console.error function
  const originalConsoleError = console.error

  // Create a queue of suppressed messages to allow analyzing actual source
  const suppressedMessages: SuppressedMessage[] = []

  // Track which files are producing errors to suppress console.error from Angular's error handler
  const fileTracker: FileTracker = {
    lastFile: '',
    setFile(file: string) {
      this.lastFile = file
    },
  }

  // Override the Error constructor to track stack traces
  const originalError = window.Error
  window.Error = function (...args: any[]): Error {
    const error = new originalError(...args)

    // If this is a CORS error message, store the stack trace
    if (
      args[0] &&
      typeof args[0] === 'string' &&
      CORS_ERROR_PATTERN.test(args[0])
    ) {
      // Find the first non-framework file in the stack to help identify the source
      const stackLines = error.stack?.split('\n') || []
      for (const line of stackLines) {
        if (
          line.includes('/app/') &&
          !line.includes('vendor.js') &&
          !line.includes('node_modules')
        ) {
          fileTracker.setFile(line.trim())
          break
        }
      }
    }

    return error
  } as ErrorConstructor

  // Override console.error to intercept CORS messages
  console.error = function (...args: any[]) {
    const firstArg = args[0]

    // Skip Angular internal error reporting when we've already handled a CORS error
    // This prevents duplicate messages from Angular's error handler
    if (
      suppressedMessages.length > 0 &&
      typeof firstArg === 'string' &&
      firstArg.startsWith('ERROR') &&
      args.some(
        (arg) =>
          arg &&
          typeof arg === 'object' &&
          arg.rejection &&
          typeof arg.rejection.message === 'string' &&
          CORS_ERROR_PATTERN.test(arg.rejection.message),
      )
    ) {
      return
    }

    // If this is a CORS error message from the browser
    if (typeof firstArg === 'string' && CORS_ERROR_PATTERN.test(firstArg)) {
      // Extract the URL that caused the CORS error
      const urlMatch = firstArg.match(URL_PATTERN)
      const url = urlMatch ? urlMatch[1] : 'unknown URL'

      // Extract the origin
      const originMatch = firstArg.match(ORIGIN_PATTERN)
      const origin = originMatch ? originMatch[1] : 'unknown origin'

      // We'll suppress the original error and show our custom message
      suppressedMessages.push({
        timestamp: new Date().toISOString(),
        message: firstArg,
        url,
        origin,
      })

      // Show our custom message
      originalConsoleError.call(
        console,
        '%c🔍 API Connection Issue (misreported as CORS)',
        'color: #e67e22; font-weight: bold; font-size: 14px; background: #FFF9C4; padding: 4px 8px; border-radius: 4px;',
      )

      originalConsoleError.call(
        console,
        `%cReporting a more accurate error instead of the misleading CORS message`,
        'color: #333; font-size: 12px;',
      )

      originalConsoleError.call(
        console,
        `%c• Request URL: ${url}\n• From origin: ${origin}\n\nLikely actual issue:\n• API endpoint is unreachable\n• API endpoint returned an error\n• Authentication failed\n• Incorrect API URL\n\nTo fix: Check the actual status in Network tab or try Postman/curl`,
        'color: #333; background: #f8f8f8; padding: 8px; border-left: 4px solid #e67e22; font-family: monospace; font-size: 12px;',
      )

      // Don't show the original CORS error message
      return
    }

    // Not a CORS error, pass through to original console.error
    originalConsoleError.apply(console, args)
  }
}
